import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Checkbox, Alert,
  CircularProgress, IconButton, InputAdornment, Divider, Chip
} from '@mui/material';
import { ArrowBack, Save, AccountBalance } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function RegistrarPago() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const proveedorIdParam = searchParams.get('proveedorId');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [comprasPendientes, setComprasPendientes] = useState([]);
  const [comprasSeleccionadas, setComprasSeleccionadas] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [montosAplicar, setMontosAplicar] = useState({});

  const [formData, setFormData] = useState({
    proveedor_id: proveedorIdParam || '',
    fecha: new Date().toISOString().split('T')[0],
    forma_pago: 'EFECTIVO',
    referencia: '',
    banco_id: '',
    banco_nombre: '',
    numero_cheque: '',
    fecha_cheque: '',
    observaciones: '',
    cuenta_contable: '',
    cuenta_contable_nombre: ''
  });

  const getConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    cargarProveedores();
    cargarCuentasContables();
    cargarBancos();
  }, []);

  useEffect(() => {
    if (formData.proveedor_id) cargarComprasPendientes(formData.proveedor_id);
  }, [formData.proveedor_id]);

  // Auto-seleccionar cuenta contable según forma de pago
  useEffect(() => {
    if (formData.forma_pago === 'EFECTIVO') {
      const caja = cuentasContables.find(c => c.codigo === '1.01.01.01' || c.nombre?.toLowerCase().includes('caja'));
      if (caja) setFormData(prev => ({ ...prev, cuenta_contable: caja.codigo, cuenta_contable_nombre: `${caja.codigo} - ${caja.nombre}`, banco_id: '', banco_nombre: '' }));
    } else {
      const banco = cuentasContables.find(c => c.codigo === '1.01.01.02' || c.nombre?.toLowerCase().includes('banco'));
      if (banco) setFormData(prev => ({ ...prev, cuenta_contable: banco.codigo, cuenta_contable_nombre: `${banco.codigo} - ${banco.nombre}` }));
    }
  }, [formData.forma_pago, cuentasContables]);

  const cargarCuentasContables = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contabilidad/plan-cuentas`, getConfig());
      const cuentas = res.data?.data || res.data || [];
      const mov = cuentas.filter(c => c.acepta_movimiento && (
        c.codigo?.startsWith('1.01.01') || c.nombre?.toLowerCase().includes('caja') || c.nombre?.toLowerCase().includes('banco')
      ));
      setCuentasContables(mov.length > 0 ? mov : cuentas.filter(c => c.acepta_movimiento));
    } catch (err) { console.error('Error cuentas:', err); }
  };

  const cargarBancos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tesoreria/bancos`, getConfig());
      setBancos(res.data?.data || res.data || []);
    } catch (err) { console.log('Bancos no disponibles'); }
  };

  const cargarProveedores = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/cuentas-pagar/proveedores-con-saldo`, getConfig());
      setProveedores(res.data.data || []);
    } catch (err) { console.error('Error proveedores:', err); }
  };

  const cargarComprasPendientes = async (proveedorId) => {
    try {
      const res = await axios.get(`${API_URL}/api/cuentas-pagar/compras-pendientes?proveedorId=${proveedorId}`, getConfig());
      setComprasPendientes(res.data.data || []);
    } catch (err) {
      console.error('Error compras:', err);
      setError('Error al cargar compras pendientes');
    }
  };

  const getSaldo = (compra) => {
    if (compra.saldo_pendiente !== undefined) return parseFloat(compra.saldo_pendiente);
    const total = parseFloat(compra.totales?.importeTotal || compra.total || 0);
    const pagado = parseFloat(compra.total_pagado || 0);
    return total - pagado;
  };

  const getTotal = (compra) => parseFloat(compra.totales?.importeTotal || compra.total || 0);

  const handleSeleccionarCompra = (compra) => {
    const isSelected = comprasSeleccionadas.some(c => c.id === compra.id);
    if (isSelected) {
      setComprasSeleccionadas(comprasSeleccionadas.filter(c => c.id !== compra.id));
      const m = { ...montosAplicar }; delete m[compra.id]; setMontosAplicar(m);
    } else {
      setComprasSeleccionadas([...comprasSeleccionadas, compra]);
      setMontosAplicar({ ...montosAplicar, [compra.id]: getSaldo(compra).toFixed(2) });
    }
  };

  const calcularTotalPago = () => Object.values(montosAplicar).reduce((s, m) => s + parseFloat(m || 0), 0);

  const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (comprasSeleccionadas.length === 0) return setError('Seleccione al menos una compra');
    if (!formData.cuenta_contable) return setError('Seleccione una cuenta contable');

    for (const compra of comprasSeleccionadas) {
      const monto = parseFloat(montosAplicar[compra.id] || 0);
      if (monto <= 0) return setError('El monto debe ser mayor a 0');
      if (monto > getSaldo(compra)) return setError('El monto excede el saldo pendiente');
    }

    try {
      setLoading(true); setError(null);
      const proveedorSeleccionado = proveedores.find(p => p.proveedor_id === formData.proveedor_id);
      const payload = {
        proveedor_id: formData.proveedor_id,
        proveedor_nombre: proveedorSeleccionado?.proveedor_nombre || '',
        fecha: formData.fecha,
        forma_pago: formData.forma_pago,
        referencia: formData.referencia,
        banco_id: formData.banco_id,
        banco_nombre: formData.banco_nombre,
        numero_cheque: formData.numero_cheque,
        fecha_cheque: formData.fecha_cheque,
        observaciones: formData.observaciones,
        cuenta_contable: formData.cuenta_contable,
        cuenta_contable_nombre: formData.cuenta_contable_nombre
      };

      let pagoResponse;
      if (comprasSeleccionadas.length === 1) {
        const compra = comprasSeleccionadas[0];
        pagoResponse = await axios.post(`${API_URL}/api/cuentas-pagar/pagos`, {
          ...payload, compra_id: compra.id, monto: parseFloat(montosAplicar[compra.id])
        }, getConfig());
      } else {
        pagoResponse = await axios.post(`${API_URL}/api/cuentas-pagar/pagos/multiple`, {
          ...payload, compras: comprasSeleccionadas.map(c => ({ compra_id: c.id, monto: parseFloat(montosAplicar[c.id]) }))
        }, getConfig());
      }

      // Obtener ID del pago para descargar PDF
      const pagoData = pagoResponse.data?.data;
      const pagoId = Array.isArray(pagoData) ? pagoData[0]?.id : pagoData?.id;

      setSuccess('Pago registrado exitosamente');

      // Descargar PDF del comprobante de pago
      if (pagoId) {
        try {
          const pdfRes = await axios.get(`${API_URL}/api/cuentas-pagar/pagos/${pagoId}/pdf`, {
            ...getConfig(), responseType: 'blob'
          });
          const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Comprobante_Pago_${formData.referencia || pagoId.substring(0, 8)}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (pdfErr) {
          console.warn('No se pudo descargar PDF de pago:', pdfErr);
        }
      }

      setTimeout(() => navigate('/cuentas-pagar'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al registrar pago');
    } finally { setLoading(false); }
  };

  const mostrarBancos = ['CHEQUE', 'TRANSFERENCIA', 'TARJETA_CREDITO'].includes(formData.forma_pago);

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/cuentas-pagar')} sx={{ mr: 1 }}><ArrowBack /></IconButton>
        <Typography variant="h5" fontWeight={700}>Registrar Pago a Proveedor</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Datos del Pago</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Proveedor</InputLabel>
                    <Select value={formData.proveedor_id} label="Proveedor"
                      onChange={(e) => { setFormData({ ...formData, proveedor_id: e.target.value }); setComprasSeleccionadas([]); setMontosAplicar({}); }}>
                      {(proveedores || []).map(p => (
                        <MenuItem key={p.proveedor_id} value={p.proveedor_id}>
                          {p.proveedor_nombre} — {fmt(p.saldo_total)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth required type="date" label="Fecha" value={formData.fecha}
                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth required>
                    <InputLabel>Forma de Pago</InputLabel>
                    <Select value={formData.forma_pago} label="Forma de Pago"
                      onChange={(e) => setFormData({ ...formData, forma_pago: e.target.value })}>
                      <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                      <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
                      <MenuItem value="CHEQUE">Cheque</MenuItem>
                      <MenuItem value="TARJETA_CREDITO">Tarjeta de Crédito</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Cuenta Contable y Banco */}
                <Grid item xs={12}><Divider><Chip label="Contabilidad" icon={<AccountBalance />} size="small" /></Divider></Grid>
                <Grid item xs={12} md={mostrarBancos ? 6 : 12}>
                  <FormControl fullWidth required>
                    <InputLabel>Cuenta Contable (Origen del dinero)</InputLabel>
                    <Select value={formData.cuenta_contable} label="Cuenta Contable (Origen del dinero)"
                      onChange={(e) => {
                        const c = cuentasContables.find(x => x.codigo === e.target.value);
                        setFormData({ ...formData, cuenta_contable: e.target.value, cuenta_contable_nombre: c ? `${c.codigo} - ${c.nombre}` : '' });
                      }}>
                      {cuentasContables.map(c => (
                        <MenuItem key={c.id || c.codigo} value={c.codigo}>{c.codigo} - {c.nombre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {formData.forma_pago === 'EFECTIVO' ? '💰 Efectivo → Sale de Caja' : '🏦 Sale de cuenta bancaria'}
                  </Typography>
                </Grid>
                {mostrarBancos && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Banco</InputLabel>
                      <Select value={formData.banco_id} label="Banco"
                        onChange={(e) => {
                          const b = bancos.find(x => x.id === e.target.value);
                          setFormData({ ...formData, banco_id: e.target.value, banco_nombre: b?.nombre || '' });
                        }}>
                        <MenuItem value="">— Sin especificar —</MenuItem>
                        {bancos.map(b => (
                          <MenuItem key={b.id} value={b.id}>{b.nombre} {b.numero_cuenta ? `(${b.numero_cuenta})` : ''}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Referencia / No. Comprobante" value={formData.referencia}
                    onChange={(e) => setFormData({ ...formData, referencia: e.target.value })} />
                </Grid>
                {formData.forma_pago === 'CHEQUE' && (
                  <>
                    <Grid item xs={6} md={3}>
                      <TextField fullWidth label="No. Cheque" value={formData.numero_cheque}
                        onChange={(e) => setFormData({ ...formData, numero_cheque: e.target.value })} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField fullWidth type="date" label="Fecha Cheque" value={formData.fecha_cheque}
                        onChange={(e) => setFormData({ ...formData, fecha_cheque: e.target.value })} InputLabelProps={{ shrink: true }} />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Observaciones" value={formData.observaciones}
                    onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Compras Pendientes */}
          {formData.proveedor_id && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Compras Pendientes de Pago</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell><strong>Fecha</strong></TableCell>
                        <TableCell><strong>No. Factura</strong></TableCell>
                        <TableCell align="right"><strong>Total Factura</strong></TableCell>
                        <TableCell align="right"><strong>Saldo Pendiente</strong></TableCell>
                        <TableCell align="right"><strong>Monto a Pagar</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!comprasPendientes || comprasPendientes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">No hay compras pendientes para este proveedor</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        comprasPendientes.map(compra => {
                          const saldo = getSaldo(compra);
                          const total = getTotal(compra);
                          const isSelected = comprasSeleccionadas.some(c => c.id === compra.id);
                          return (
                            <TableRow key={compra.id} hover selected={isSelected}>
                              <TableCell padding="checkbox">
                                <Checkbox checked={isSelected} onChange={() => handleSeleccionarCompra(compra)} />
                              </TableCell>
                              <TableCell>{compra.fecha_emision || new Date(compra.created_at).toLocaleDateString('es-EC')}</TableCell>
                              <TableCell>{compra.factura?.numeroComprobante || '—'}</TableCell>
                              <TableCell align="right">{fmt(total)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>{fmt(saldo)}</TableCell>
                              <TableCell align="right">
                                {isSelected && (
                                  <TextField type="number" size="small" value={montosAplicar[compra.id] || ''}
                                    onChange={(e) => setMontosAplicar({ ...montosAplicar, [compra.id]: e.target.value })}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    inputProps={{ min: 0, max: saldo, step: 0.01 }}
                                    sx={{ width: 130 }} />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {comprasSeleccionadas.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">{comprasSeleccionadas.length} compra(s) seleccionada(s)</Typography>
                    <Typography variant="h6" fontWeight={700} color="error.main">Total a Pagar: {fmt(calcularTotalPago())}</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={() => navigate('/cuentas-pagar')} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="contained" size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                disabled={loading || comprasSeleccionadas.length === 0}>
                {loading ? 'Guardando...' : 'Registrar Pago'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}

export default RegistrarPago;
